import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { CompanyConfiguration, configurationService } from '../services/configuration.service';
import { APP_BRAND, withAppBranding } from '../constants/branding';

interface BrandingContextType {
  branding: CompanyConfiguration | null;
  isLoading: boolean;
  refreshBranding: () => Promise<void>;
}

const BrandingContext = createContext<BrandingContextType | undefined>(undefined);

interface BrandingProviderProps {
  children: ReactNode;
}

export const BrandingProvider: React.FC<BrandingProviderProps> = ({ children }) => {
  const [branding, setBranding] = useState<CompanyConfiguration | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadBranding = async () => {
    try {
      setIsLoading(true);
      const config = withAppBranding(await configurationService.getConfiguration());
      setBranding(config);
      
      // Apply branding to document
      applyBranding(config);
      
      // Store in localStorage for quick access
      localStorage.setItem('companyBranding', JSON.stringify(config));
    } catch (error) {
      console.error('❌ Failed to load branding:', error);
      
      // Try to load from localStorage as fallback
      const cached = localStorage.getItem('companyBranding');
      if (cached) {
        console.log('⚠️  Using cached branding');
        const config = withAppBranding(JSON.parse(cached));
        setBranding(config);
        applyBranding(config);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const applyBranding = (config: CompanyConfiguration | null) => {
    if (!config) return;
    
    // Apply theme mode
    if (config.theme_mode) {
      document.documentElement.setAttribute('data-theme', config.theme_mode);
      if (config.theme_mode === 'light') {
        document.documentElement.classList.add('light-theme');
        document.documentElement.classList.remove('dark-theme');
      } else {
        document.documentElement.classList.add('dark-theme');
        document.documentElement.classList.remove('light-theme');
      }
    }
    
    // Apply favicon
    if (config.favicon_url) {
      const favicon = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
      if (favicon) {
        favicon.href = config.favicon_url;
      }
    }

    // Apply CSS custom properties for colors
    if (config.primary_color) {
      document.documentElement.style.setProperty('--brand-primary', config.primary_color);
      document.documentElement.style.setProperty('--brand-primary-rgb', hexToRgb(config.primary_color));
      // Create darker hover variant (reduce lightness by 10%)
      const hoverColor = adjustColorBrightness(config.primary_color, -10);
      document.documentElement.style.setProperty('--brand-primary-hover', hoverColor);
    }
    if (config.secondary_color) {
      document.documentElement.style.setProperty('--brand-secondary', config.secondary_color);
      document.documentElement.style.setProperty('--brand-secondary-rgb', hexToRgb(config.secondary_color));
      // Create darker hover variant
      const hoverColor = adjustColorBrightness(config.secondary_color, -10);
      document.documentElement.style.setProperty('--brand-secondary-hover', hoverColor);
    }
    
    // Update document title
    document.title = APP_BRAND.name;
  };

  const hexToRgb = (color: string): string => {
    const value = color.replace('#', '');
    const hex = value.length === 3 ? value.split('').map(char => char + char).join('') : value;
    return [0, 2, 4].map(offset => parseInt(hex.substring(offset, offset + 2), 16)).join(' ');
  };

  // Helper function to adjust color brightness
  const adjustColorBrightness = (color: string, percent: number): string => {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    const adjust = (value: number) => {
      const adjusted = value + (value * percent) / 100;
      return Math.min(255, Math.max(0, Math.round(adjusted)));
    };

    const newR = adjust(r).toString(16).padStart(2, '0');
    const newG = adjust(g).toString(16).padStart(2, '0');
    const newB = adjust(b).toString(16).padStart(2, '0');

    return `#${newR}${newG}${newB}`;
  };

  const refreshBranding = async () => {
    await loadBranding();
  };

  useEffect(() => {
    document.title = APP_BRAND.name;

    // Set default dark theme
    document.documentElement.classList.add('dark-theme');
    document.documentElement.setAttribute('data-theme', 'dark');
    
    // Load branding on mount if user is authenticated
    const token = localStorage.getItem('authToken');
    if (token) {
      loadBranding();
    } else {
      setIsLoading(false);
    }
  }, []);

  return (
    <BrandingContext.Provider value={{ branding, isLoading, refreshBranding }}>
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = (): BrandingContextType => {
  const context = useContext(BrandingContext);
  if (context === undefined) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};
